import { useCallback, useReducer } from 'react'
import { createContainer } from 'unstated-next'

import {
  fetchGames as getGames,
  fetchProviders as getProviders,
  fetchCategories as getCategories,
  fetchGameTypes as getGameTypes,
  fetchFavoriteGames as getFavorites
} from '../api/casino'

import {
  CASINO_STATE_UPDATE,
  CASINO_LAST_STATE_UPDATE,
  CASINO_GAME_ADD,
  CASINO_FAVORITES_ADD,
  CASINO_GAME_UPDATE,
  CASINO_FAVORITE_GAME_UPDATE,
  CASINO_STATE_TOGGLE
} from '../actions'

const initialState = {
  games: [],
  topGames: [],
  favorites: [],
  categories: [],
  providers: [],
  topProviders: [],
  gameTypes: [],
  paging: {
    itemsPerPage: 21,
    currentPage: 0
  },
  favoritesPaging: {
    itemsPerPage: 21,
    currentPage: 0
  },
  filter: {},
  isSearchActive: false,
  isFilterActive: false,
  showFavorites: false,
  lastRequestTime: null,
  categoriesResolved: false,
  resolved: false,
  loading: false,
  error: null
}

const casinoGameReducer = (state, action) => {
  switch (action.type) {
    case CASINO_STATE_UPDATE: {
      const { payload } = action

      return {
        ...state,
        ...payload
      }
    }
    case CASINO_STATE_TOGGLE: {
      const { payload } = action

      return {
        ...state,
        [payload]: !state[payload]
      }
    }
    case CASINO_LAST_STATE_UPDATE: {
      const { games, paging, resolved, loading, lastRequestTime } = action.payload

      if (lastRequestTime === state.lastRequestTime) {
        return {
          ...state,
          games,
          paging,
          resolved,
          loading
        }
      } else {
        return state
      }
    }
    case CASINO_GAME_ADD: {
      const { games: newGames, paging, lastRequestTime } = action.payload
      const { games: oldGames } = state

      if (lastRequestTime === state.lastRequestTime) {
        return {
          ...state,
          games: [
            ...oldGames,
            ...newGames
          ],
          paging: {
            ...paging
          },
          resolved: true,
          loading: false
        }
      } else {
        return state
      }
    }
    case CASINO_GAME_UPDATE: {
      const { games } = state
      const updatedGames = games.map((item) => (
        item.id === action.payload.id ? action.payload : item
      ))

      return {
        ...state,
        games: updatedGames
      }
    }
    case CASINO_FAVORITE_GAME_UPDATE: {
      const { favorites } = state
      const updatedFavorites = favorites.map((item) => (
        item.id === action.payload.id ? action.payload : item
      ))

      return {
        ...state,
        favorites: updatedFavorites
      }
    }
    case CASINO_FAVORITES_ADD: {
      const { favorites: newGames, paging } = action.payload
      const { favorites: oldGames } = state

      return {
        ...state,
        favorites: [
          ...oldGames,
          ...newGames
        ],
        favoritesPaging: {
          ...paging
        },
        resolved: true,
        loading: false
      }
    }
    default:
      return state
  }
}

function useCasino () {
  const [state, dispatch] = useReducer(casinoGameReducer, initialState)

  const fetchGames = useCallback((options) => {
    const lastRequestTime = Date.now()
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        lastRequestTime: lastRequestTime,
        resolved: false,
        loading: true,
        filter: options.filter
      }
    })

    let filter = options

    if (!filter) {
      filter = {
        filter: state.filters,
        paging: {
          itemsPerPage: state.paging.itemsPerPage,
          currentPage: 0
        }
      }
    }

    getGames(filter)
      .then((res) => {
        dispatch({
          type: CASINO_LAST_STATE_UPDATE,
          payload: {
            paging: res.paging,
            games: res.games,
            resolved: true,
            loading: false,
            lastRequestTime
          }
        })
      })
      .catch((err) => {
        dispatch({
          type: CASINO_STATE_UPDATE,
          payload: {
            error: err,
            resolved: true,
            loading: false
          }
        })
      })
  }, [])

  const fetchMoreGames = useCallback((config) => {
    if (state.paging.currentPage >= state.paging.totalPages - 1) {
      return
    }
    const lastRequestTime = Date.now()

    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        loading: true,
        lastRequestTime
      }
    })

    const options = {
      ...config,
      paging: {
        itemsPerPage: state.paging.itemsPerPage,
        currentPage: state.paging.currentPage + 1
      },
      filter: state.filter
    }

    getGames(options)
      .then((res) => {
        dispatch({
          type: CASINO_GAME_ADD,
          payload: {
            paging: res.paging,
            games: res.games,
            resolved: true,
            loading: false,
            lastRequestTime
          }
        })
      })
      .catch((err) => {
        dispatch({
          type: CASINO_STATE_UPDATE,
          payload: {
            error: err,
            loading: false
          }
        })
      })
  }, [state.paging, state._filter])

  const fetchFavorites = useCallback((options) => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        resolved: false,
        loading: true
      }
    })

    getFavorites(options)
      .then((res) => {
        dispatch({
          type: CASINO_STATE_UPDATE,
          payload: {
            favoritesPaging: res.paging,
            favorites: res.games,
            resolved: true,
            loading: false
          }
        })
      })
      .catch((err) => {
        dispatch({
          type: CASINO_STATE_UPDATE,
          payload: {
            error: err,
            resolved: true,
            loading: false
          }
        })
      })
  }, [])

  const fetchMoreFavorites = useCallback((config) => {
    if (state.favoritesPaging.currentPage >= state.favoritesPaging.totalPages - 1) {
      return
    }

    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        loading: true
      }
    })

    const options = {
      ...config,
      paging: {
        itemsPerPage: state.favoritesPaging.itemsPerPage,
        currentPage: state.favoritesPaging.currentPage + 1
      }
    }

    getFavorites(options)
      .then((res) => {
        dispatch({
          type: CASINO_FAVORITES_ADD,
          payload: {
            paging: res.paging,
            favorites: res.games,
            resolved: true,
            loading: false
          }
        })
      })
      .catch((err) => {
        dispatch({
          type: CASINO_STATE_UPDATE,
          payload: {
            error: err,
            loading: false
          }
        })
      })
  }, [state.favoritesPaging])

  const fetchCategories = useCallback(async (options) => {
    const res = await getCategories(options)
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        categories: res,
        categoriesResolved: true
      }
    })
  }, [])

  const fetchGameTypes = useCallback(async (options) => {
    const res = await getGameTypes(options)

    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        gameTypes: res
      }
    })
  }, [])

  const fetchProviders = useCallback(async (options) => {
    const res = await getProviders(options)

    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        providers: res.providers
      }
    })
  }, [])

  const fetchTopGames = useCallback(async (options) => {
    const res = await getGames(options)

    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        topGames: res.games
      }
    })
  }, [])

  const updateGame = useCallback((game) => {
    dispatch({
      type: CASINO_GAME_UPDATE,
      payload: game
    })
  }, [])

  const updateFavoriteGame = useCallback((game) => {
    dispatch({
      type: CASINO_FAVORITE_GAME_UPDATE,
      payload: game
    })
  }, [])

  const clearGames = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        games: [],
        resolved: false
      }
    })
  }, [])

  const updateFilter = useCallback((filter) => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        filter
      }
    })
  }, [])

  const showFavorites = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        showFavorites: true
      }
    })
  }, [])

  const hideFavorites = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        showFavorites: false
      }
    })
  }, [])

  const toggleIsFilterActive = useCallback(() => {
    dispatch({
      type: CASINO_STATE_TOGGLE,
      payload: 'isFilterActive'
    })
  }, [])

  const toggleIsSearchActive = useCallback(() => {
    dispatch({
      type: CASINO_STATE_TOGGLE,
      payload: 'isSearchActive'
    })
  }, [])

  const clearTopGames = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        topGames: [],
        resolved: false
      }
    })
  }, [])

  const clearCategories = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        categories: []
      }
    })
  }, [])

  const clearGameTypes = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        gameTypes: []
      }
    })
  }, [])

  const clearProviders = useCallback(() => {
    dispatch({
      type: CASINO_STATE_UPDATE,
      payload: {
        providers: [],
        topProviders: []
      }
    })
  }, [])

  return {
    state,
    fetchGames,
    fetchMoreGames,
    fetchFavorites,
    fetchMoreFavorites,
    fetchTopGames,
    fetchProviders,
    fetchCategories,
    fetchGameTypes,
    updateGame,
    updateFilter,
    updateFavoriteGame,
    showFavorites,
    hideFavorites,
    toggleIsFilterActive,
    toggleIsSearchActive,
    clearGames,
    clearTopGames,
    clearCategories,
    clearGameTypes,
    clearProviders
  }
}

export const Casino = createContainer(useCasino)
